import { roundValueUp } from "@lib/roundValue";
import { MVF, PiecewisePoint } from "@lib/tranche";
import { ldtFromMVF } from "@lib/tranche/trancheUtils";
import { clamp } from "@lib/utils/clamp";
import { captureMessage } from "@sentry/react";
import * as d3 from "d3";
import { FrontendEvaluation, MVFUpdate } from "../useEvaluatorEvaluationsCache";
import { DraggableCircle } from "./DraggableCircle";
import { D3Axis } from "./MVFGraph";

function fdvDragHandler(
  event: d3.D3DragEvent<Element, unknown, null>,
  evaluation: FrontendEvaluation,
  mvfupdate: MVFUpdate,
  y: D3Axis,
) {
  const mvf = evaluation.mvf;

  let newY = clamp(y.invert(event.y), mvf[1]?.value || 0, 10);

  newY = roundValueUp(newY);

  const newMvf = mvf.map((p, i) => (i === 0 ? { ...p, value: newY } : p));
  mvfupdate(evaluation.evaluatorId, evaluation.evaluableId, newMvf);
}

function getNewPiecewiseLdt(
  previousPoint: { dollar: number; value: number },
  newX: number,
  newY: number,
): number {
  if (newX < previousPoint.dollar) {
    return previousPoint.dollar;
  }

  if (newY >= previousPoint.value) {
    return 50000000;
  }

  const a = previousPoint.value - newY;
  const b = previousPoint.dollar - newX;

  // Slope of the line
  const m = a / b;

  // y = mx + c where c is unknown
  // Use any of the given point to find c
  const c = previousPoint.value - m * previousPoint.dollar;

  // For finding the x-intercept put y = 0
  const newDollar = (0 - c) / (m * 1);

  return Math.trunc(newDollar);
}

function pointDragHandler(
  event: d3.D3DragEvent<Element, unknown, null>,
  evaluation: FrontendEvaluation,
  mvfUpdate: MVFUpdate,
  index: number,
  x: D3Axis,
  y: D3Axis,
): void {
  const mvf = evaluation.mvf;

  let newY = clamp(
    y.invert(event.y),
    mvf[index + 1]?.value || 0,
    mvf[index - 1]?.value || 10,
  );

  let newX = clamp(
    x.invert(event.x),
    mvf[index - 1]?.dollar || 0,
    mvf[index + 1]?.dollar || 50000000,
  );

  newY = roundValueUp(newY);
  newX = Math.trunc(newX);

  const newMvf = mvf.map((p, i) =>
    i === index ? { dollar: newX, value: newY, placedManually: true } : p,
  );

  mvfUpdate(evaluation.evaluatorId, evaluation.evaluableId, newMvf);
}

function ldtDragHandler(
  event: d3.D3DragEvent<Element, unknown, null>,
  evaluation: FrontendEvaluation,
  mvfUpdate: MVFUpdate,
  x: D3Axis,
  y: D3Axis,
) {
  const mvf = evaluation.mvf;

  const newY = roundValueUp(Math.max(y.invert(event.y), 0));
  const newX = x.invert(event.x);

  const previousPoint = mvf[mvf.length - 2];
  if (!previousPoint) {
    // TODO: error
    return null;
  }

  const newDollar = getNewPiecewiseLdt(previousPoint, newX, newY);

  const newMvf = mvf.map((p, i) =>
    i === mvf.length - 1 ? { ...p, dollar: newDollar } : p,
  );

  mvfUpdate(evaluation.evaluatorId, evaluation.evaluableId, newMvf);
}

export function piecewiseLineClickHandler(
  event: MouseEvent,
  evaluation: FrontendEvaluation,
  mvfUpdate: MVFUpdate,
  xAxis: D3Axis,
  yAxis: D3Axis,
) {
  event.stopPropagation();
  const mvf = evaluation.mvf;

  const [mouseX, mouseY] = d3.pointer(event);

  const previousPointIndex = mvf.findLastIndex(
    (point) => point.dollar < xAxis.invert(mouseX),
  );
  const nextPointIndex = previousPointIndex + 1;

  const dollar = xAxis.invert(mouseX);
  const value = roundValueUp(yAxis.invert(mouseY));

  if (
    previousPointIndex < 0 ||
    previousPointIndex >= mvf.length ||
    nextPointIndex < 0 ||
    nextPointIndex >= mvf.length
  ) {
    console.error(
      "previousPointIndex and/or nextPoint out of bounds",
      previousPointIndex,
      nextPointIndex,
      mvf,
    );
    captureMessage("previousPointIndex and/or nextPoint out of bounds", {
      level: "error",
    });
    return;
  }

  if (
    dollar < mvf[previousPointIndex].dollar ||
    dollar > mvf[nextPointIndex].dollar
  ) {
    return;
  }

  if (
    value < mvf[nextPointIndex].value ||
    value > mvf[previousPointIndex].value
  ) {
    return;
  }

  const newMvf: MVF = [
    ...mvf.slice(0, nextPointIndex),
    { dollar, value, placedManually: true },
    ...mvf.slice(nextPointIndex),
  ];

  mvfUpdate(evaluation.evaluatorId, evaluation.evaluableId, newMvf);
}

function pointDoubleClickHandler(
  event: MouseEvent,
  evaluation: FrontendEvaluation,
  mvfUpdate: MVFUpdate,
  xAxis: D3Axis,
  yAxis: D3Axis,
): void {
  event.stopPropagation();
  const mvf = evaluation.mvf;

  const [mouseX, mouseY] = d3.pointer(event);

  let closestIndex = -1;
  let closestDistance = 100000;

  for (let index = 0; index < mvf.length; index++) {
    const point = mvf[index];
    const dx = xAxis(point.dollar) - mouseX;
    const dy = yAxis(point.value) - mouseY;

    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  }

  if (closestIndex === -1) {
    return;
  }

  const newPoints = [...mvf];
  newPoints.splice(closestIndex, 1);

  mvfUpdate(evaluation.evaluatorId, evaluation.evaluableId, newPoints);
}

export function isPointVisible(
  point: PiecewisePoint,
  xDomain: [number, number],
  yDomain: [number, number],
) {
  return (
    point.dollar >= xDomain[0] &&
    point.dollar <= xDomain[1] &&
    point.value >= yDomain[0] &&
    point.value <= yDomain[1]
  );
}

export function PiecewiseControls({
  mvfUpdate,
  evaluation,
  xAxis,
  yAxis,
  color,
}: {
  evaluation: FrontendEvaluation;
  mvfUpdate: MVFUpdate;
  xAxis: D3Axis;
  yAxis: D3Axis;
  color: string;
}) {
  const mvf = evaluation.mvf;

  const xDomain = xAxis.domain() as [number, number];
  const yDomain = yAxis.domain() as [number, number];

  const visiblePoints = mvf.slice(1, mvf.length - 1).filter((point) => {
    return isPointVisible(point, xDomain, yDomain);
  });

  const fdvPoint = mvf[0];
  const isFdvPointVisible = isPointVisible(fdvPoint, xDomain, yDomain);

  const ldtPoint = mvf[mvf.length - 1];
  const isLdtPointVisible = isPointVisible(ldtPoint, xDomain, yDomain);

  return (
    <>
      {isFdvPointVisible && (
        <DraggableCircle
          dragHandler={(event) => {
            fdvDragHandler(event, evaluation, mvfUpdate, yAxis);
          }}
          point={fdvPoint}
          xAxis={xAxis}
          yAxis={yAxis}
          color={color}
          title={`[${fdvPoint.dollar}, ${fdvPoint.value}]`}
        />
      )}
      {visiblePoints.map((point, index) => (
        <DraggableCircle
          key={`${index}`}
          doubleClickHandler={(event) => {
            pointDoubleClickHandler(event, evaluation, mvfUpdate, xAxis, yAxis);
          }}
          dragHandler={(event) => {
            pointDragHandler(
              event,
              evaluation,
              mvfUpdate,
              index + 1,
              xAxis,
              yAxis,
            );
          }}
          point={point}
          xAxis={xAxis}
          yAxis={yAxis}
          color={color}
          title={`$${point.dollar} - ${point.value}`}
        />
      ))}
      {isLdtPointVisible && (
        <DraggableCircle
          dragHandler={(event) => {
            ldtDragHandler(event, evaluation, mvfUpdate, xAxis, yAxis);
          }}
          point={ldtPoint}
          xAxis={xAxis}
          yAxis={yAxis}
          color={color}
          title={`[${ldtPoint.dollar}, ${ldtPoint.value}]`}
        />
      )}
    </>
  );
}

export function HoldControls({
  mvfUpdate,
  evaluation,
  xAxis,
  yAxis,
  color,
}: {
  evaluation: FrontendEvaluation;
  mvfUpdate: MVFUpdate;
  xAxis: D3Axis;
  yAxis: D3Axis;
  color: string;
}) {
  const xDomain = xAxis.domain() as [number, number];
  const mvf = evaluation.mvf;
  const dollar = (xDomain[0] + Math.min(ldtFromMVF(mvf), xDomain[1])) / 2;

  return (
    <>
      <DraggableCircle
        dragHandler={(event) => {
          holdDragHandler(event, evaluation, mvfUpdate, yAxis);
        }}
        xAxis={xAxis}
        yAxis={yAxis}
        point={{ dollar, value: mvf[0].value }}
        color={color}
        title={mvf[0].value.toString()}
      />
    </>
  );
}

function holdDragHandler(
  event: d3.D3DragEvent<Element, unknown, null>,
  evaluation: FrontendEvaluation,
  mvfupdate: MVFUpdate,
  y: D3Axis,
) {
  // TODO: TrancheEditor blows up if hold is set to 0
  let newY = clamp(y.invert(event.y), 0.001, 10);

  newY = roundValueUp(newY);

  const points = [
    { dollar: 0, value: newY, placedManually: true },
    { dollar: 1e12, value: newY, placedManually: true },
    { dollar: 1e12, value: 0, placedManually: true },
  ];

  const newMvf: MVF = points;
  mvfupdate(evaluation.evaluatorId, evaluation.evaluableId, newMvf);
}
